import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { getAdminUsers, AdminUserData } from '../features/admin/api';
import Table from '../components/ui/table';
import type { Column } from '../components/ui/table';
import Button from '../components/ui/button';
import AdminCsvExportButton from '../features/admin/AdminCsvExportButton';

const PAGE_SIZE = 20;

type SortKey = keyof AdminUserData;
type SortState = { key: SortKey; dir: 'asc' | 'desc' };

const AdminPanel: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState<AdminUserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', dir: 'desc' }); // Default: newest first

  useEffect(() => {
    if (!authLoading && (!user || !user.isAdmin)) {
      showToast({ title: 'Access Denied', message: 'Admin access required. Redirecting to homepage.', type: 'error' });
      navigate('/');
    }
  }, [authLoading, user, navigate, showToast]);

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      const users = await getAdminUsers();
      setRows(users);
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error || e.message || 'Failed to load admin data.';
      showToast({ title: 'Error', message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (user && !authLoading && user.isAdmin) {
      fetchAdminData();
    }
  }, [user, authLoading, fetchAdminData]);

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const aVal = (a as any)[sort.key];
      const bVal = (b as any)[sort.key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sort.dir === 'asc' ? -1 : 1;
      if (bVal == null) return sort.dir === 'asc' ? 1 : -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') return sort.dir === 'asc' ? aVal - bVal : bVal - aVal;
      return sort.dir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return sorted;
  }, [rows, sort]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, page]);

  const onSort = (key: SortKey) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' });
  };

  // Sortable header renderer with inline sort icon
  const renderHeader = (line1: string, line2: string, key: SortKey) => {
    const hasSecond = line2 && line2.trim() !== '' && line2 !== '\u00A0';
    const SortedIcon = () => (
      <>
        <span className="text-slate-400 group-hover:text-emerald-500 text-xs">↕</span>
        {sort.key === key && (
          <span className="text-emerald-600 font-bold">{sort.dir === 'asc' ? '↑' : '↓'}</span>
        )}
      </>
    );

    return (
      <button
        className="text-left hover:text-emerald-700 transition-colors cursor-pointer font-medium group leading-tight"
        onClick={() => onSort(key)}
        title="Click to sort"
      >
        {hasSecond ? (
          <>
            <span className="block">{line1}</span>
            <span className="inline-flex items-center gap-1">
              <span>{line2}</span>
              <SortedIcon />
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1">
              <span>{line1}</span>
              <SortedIcon />
            </span>
            <span className="block">&nbsp;</span>
          </>
        )}
      </button>
    );
  };

  // Format created at date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Reset time to midnight for comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
    
    if (dateOnly.getTime() === todayOnly.getTime()) {
      return 'Today';
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return 'Yesterday';
    } else {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }
  };

  const columns: Column<AdminUserData>[] = [
    {
      key: 'username',
      header: renderHeader('Username', '\u00A0', 'username'),
      className: "min-w-[12ch]",
      render: (r: AdminUserData) => (
        <div>
          <div className="font-medium">{r.username}</div>
          {r.isAdmin && <span className="text-xs text-emerald-600 font-medium">ADMIN</span>}
        </div>
      )
    },
    {
      key: 'email',
      header: renderHeader('Email', 'Address', 'email'),
      className: "min-w-[16ch]",
      render: (r: AdminUserData) => (
        <span className="text-sm font-mono">{r.email}</span>
      )
    },
    {
      key: 'createdAt',
      header: renderHeader('Date of', 'Creation', 'createdAt'),
      className: "min-w-[10ch]",
      render: (r: AdminUserData) => formatDate(r.createdAt)
    },
    {
      key: 'lastAccess',
      header: renderHeader('Last', 'Access', 'lastAccess'),
      className: "min-w-[10ch]",
      render: (r: AdminUserData) => r.lastAccess ? formatDate(r.lastAccess) : 'Never'
    },
    {
      key: 'friendCount',
      header: renderHeader('Friends', 'Created', 'friendCount'),
      className: "min-w-[8ch] text-center",
      render: (r: AdminUserData) => (
        <div className="text-center">
          <span className="text-lg font-semibold">{r.friendCount}</span>
        </div>
      )
    },
    {
      key: 'journeysGenerated',
      header: renderHeader('Journeys', 'Generated', 'journeysGenerated'),
      className: "min-w-[8ch] text-center",
      render: (r: AdminUserData) => (
        <div className="text-center">
          <span className="text-lg font-semibold text-emerald-600">{r.journeysGenerated}</span>
        </div>
      )
    },
    {
      key: 'journeyCount',
      header: renderHeader('Journeys', 'Saved', 'journeyCount'),
      className: "min-w-[8ch] text-center",
      render: (r: AdminUserData) => (
        <div className="text-center">
          <span className="text-lg font-semibold">{r.journeyCount}</span>
        </div>
      )
    },
    {
      key: 'loginCount',
      header: renderHeader('Login', 'Count', 'loginCount'),
      className: "min-w-[8ch] text-center",
      render: (r: AdminUserData) => (
        <div className="text-center">
          <span className="text-lg font-semibold">{r.loginCount}</span>
        </div>
      )
    },
    {
      // Not sortable: an array has no meaningful sort order, so this column
      // renders a plain header instead of going through renderHeader.
      key: 'interests',
      header: <span className="font-medium leading-tight"><span className="block">Interests</span><span className="block">&nbsp;</span></span>,
      className: "min-w-[16ch]",
      render: (r: AdminUserData) => {
        if (!r.interests || r.interests.length === 0) {
          return <span className="text-slate-500 text-xs">none</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {r.interests.map((interest) => {
              // The 'other' chip carries the user's own words.
              const label =
                interest === 'other' && r.interestsOther
                  ? `other: ${r.interestsOther.length > 40 ? `${r.interestsOther.slice(0, 40)}…` : r.interestsOther}`
                  : interest.replace(/_/g, ' ');
              return (
                <span
                  key={interest}
                  title={interest === 'other' ? r.interestsOther ?? undefined : undefined}
                  className="px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200"
                >
                  {label}
                </span>
              );
            })}
          </div>
        );
      }
    }
  ];

  if (authLoading || !user || !user.isAdmin) return null;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Admin Panel</h1>
        <div className="text-sm text-slate-600 text-right">
          <div>Total Users: {rows.length}</div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => navigate('/admin/carbon-accounting')}>
          Carbon Accounting · Test Page
        </Button>
        <Button variant="secondary" onClick={() => navigate('/admin/affiliate')}>
          Omio affiliate
        </Button>
        <Button variant="secondary" onClick={() => navigate('/admin/emails')}>
          Emails · Test &amp; Log
        </Button>
        <Button variant="secondary" onClick={() => navigate('/admin/llm-requests')}>
          LLM Requests · Log
        </Button>
        <Button variant="secondary" onClick={() => navigate('/admin/waitlist')}>
          Waitlist · Archive
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-600">Loading admin data...</div>
      ) : (
        <>
          <div className="flex justify-end mb-2">
            <AdminCsvExportButton table="users" label="users" />
          </div>
          <div className="table-compact">
            <Table
              columns={columns}
              data={pagedRows}
              emptyMessage="No users found."
            />
          </div>
        </>
      )}

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-slate-600">
          Page {page} of {Math.max(1, Math.ceil(rows.length / PAGE_SIZE))}
        </div>
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            disabled={page === 1} 
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button 
            variant="secondary" 
            disabled={page >= Math.ceil(rows.length / PAGE_SIZE)} 
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
